import { Injectable, signal } from '@angular/core';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration: number;
  leaving: boolean;
}

const EXIT_DELAY = 350; // ms — must match CSS exit animation

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _toasts = signal<Toast[]>([]);
  readonly toasts = this._toasts.asReadonly();

  success(title: string, message?: string, duration = 4000) {
    this.add('success', title, message, duration);
  }

  error(title: string, message?: string, duration = 6000) {
    this.add('error', title, message, duration);
  }

  warning(title: string, message?: string, duration = 5000) {
    this.add('warning', title, message, duration);
  }

  info(title: string, message?: string, duration = 4000) {
    this.add('info', title, message, duration);
  }

  dismiss(id: string) {
    this._toasts.update(list =>
      list.map(t => t.id === id ? { ...t, leaving: true } : t)
    );
    setTimeout(() => {
      this._toasts.update(list => list.filter(t => t.id !== id));
    }, EXIT_DELAY);
  }

  private add(type: ToastType, title: string, message?: string, duration = 4000) {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const toast: Toast = { id, type, title, message, duration, leaving: false };

    this._toasts.update(list => [...list, toast]);

    if (duration > 0) {
      setTimeout(() => this.dismiss(id), duration);
    }
  }
}
